// ─── Inventario maestro — lógica PURA (parser, validación, preview) ─────────
//
// El dueño sube UNA vez su inventario maestro (Excel/CSV con Insumo | Unidad |
// Stock | Mínimo | Costo unitario | Categoría) y mezo descuenta solo cada venta
// según la receta estándar de cada producto. Todo lo que no necesita red vive
// aquí para poder testearlo sin montar React ni servicios:
//   - normalización de nombres (sin tildes / mayúsculas / espacios dobles)
//   - números "a la colombiana" ("1.250,5", "$ 4.500")
//   - unidades sinónimas (gr/g/gramos → g, und/unidad → ud, lt → L…)
//   - validación fila por fila (stock no numérico, unidad desconocida,
//     duplicados) y preview de qué pasa con cada ítem según el modo
//   - cobertura de recetas (qué productos NO descuentan nada al venderse)

// ─── Normalización ──────────────────────────────────────────────────────────

// "  Café  en GRANO " → "cafe en grano". Es la clave con la que se empatan las
// filas del Excel contra los ítems que ya existen.
export function normalizarNombre(s) {
  return String(s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Número tolerante al Excel colombiano y a celdas con símbolo de pesos:
//   "1.250,5" → 1250.5 · "0,5" → 0.5 · "$ 4.500" → 4500 · "12" → 12 · 12 → 12
// Devuelve NaN si no es un número (para distinguir "vacío/texto" de 0).
export function parseNumero(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  let s = String(v ?? '').trim();
  if (!s) return NaN;
  s = s.replace(/[$\s]/g, '');
  if (/^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) {
    // miles con punto, decimal con coma
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (/^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) {
    // miles con coma, decimal con punto (Excel en inglés)
    s = s.replace(/,/g, '');
  } else {
    s = s.replace(',', '.');
  }
  if (!/^-?\d+(\.\d+)?$/.test(s)) return NaN;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : NaN;
}

// Unidades canónicas (las mismas de inventarioService.UNIDADES) y sus sinónimos.
export const UNIDADES_CANONICAS = ['g', 'kg', 'ml', 'L', 'ud', 'lb', 'oz'];

const SINONIMOS_UNIDAD = {
  g:  ['g', 'gr', 'grs', 'gramo', 'gramos', 'gm'],
  kg: ['kg', 'kgs', 'kilo', 'kilos', 'kilogramo', 'kilogramos'],
  ml: ['ml', 'mls', 'mililitro', 'mililitros', 'cc'],
  L:  ['l', 'lt', 'lts', 'litro', 'litros'],
  ud: ['ud', 'uds', 'und', 'unds', 'un', 'u', 'unidad', 'unidades', 'pza', 'pzas', 'pieza', 'piezas', 'paquete', 'paquetes', 'botella', 'botellas'],
  lb: ['lb', 'lbs', 'libra', 'libras'],
  oz: ['oz', 'onza', 'onzas'],
};

const MAPA_UNIDAD = (() => {
  const m = new Map();
  for (const [canon, lista] of Object.entries(SINONIMOS_UNIDAD)) {
    for (const alias of lista) m.set(alias, canon);
  }
  return m;
})();

// "Gramos" → 'g' · "UND." → 'ud' · "litro" → 'L' · "" → null · "bolsa" → null
export function normalizarUnidad(u) {
  const n = normalizarNombre(u).replace(/\.$/, '');
  if (!n) return null;
  return MAPA_UNIDAD.get(n) ?? null;
}

// ─── Parser CSV / TSV (texto pegado o archivo .csv) ─────────────────────────

// Separa una línea respetando comillas dobles (y comillas escapadas "").
function splitLinea(linea, sep) {
  const out = [];
  let cur = '';
  let enComillas = false;
  for (let i = 0; i < linea.length; i++) {
    const ch = linea[i];
    if (enComillas) {
      if (ch === '"') {
        if (linea[i + 1] === '"') { cur += '"'; i++; }
        else enComillas = false;
      } else cur += ch;
    } else if (ch === '"') {
      enComillas = true;
    } else if (ch === sep) {
      out.push(cur); cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

// Texto → matriz de celdas. Detecta el separador (tab, ";" o ",") por la
// primera línea no vacía. El ";" es el CSV que exporta Excel en es-CO.
export function parsearTextoMaestro(texto) {
  const lineas = String(texto ?? '').split(/\r?\n/).filter((l) => l.trim());
  if (!lineas.length) return [];
  const primera = lineas[0];
  const cuenta = (ch) => (primera.match(new RegExp(ch === '\t' ? '\t' : `\\${ch}`, 'g')) || []).length;
  const tabs = cuenta('\t'), pyc = cuenta(';'), comas = cuenta(',');
  const sep = tabs >= pyc && tabs >= comas ? '\t' : pyc >= comas ? ';' : ',';
  return lineas.map((l) => splitLinea(l, sep));
}

// ─── Encabezados → columnas ─────────────────────────────────────────────────

const ALIAS_COLUMNAS = {
  nombre:    ['insumo', 'insumos', 'nombre', 'item', 'items', 'ingrediente', 'producto', 'descripcion', 'articulo'],
  unidad:    ['unidad', 'unidades', 'und', 'um', 'medida', 'unidad de medida'],
  stock:     ['stock', 'stock actual', 'cantidad', 'existencia', 'existencias', 'inventario', 'conteo', 'cant'],
  minimo:    ['minimo', 'min', 'stock minimo', 'minimo stock', 'punto de pedido'],
  costo:     ['costo', 'costo unitario', 'precio costo', 'precio', 'valor', 'valor unitario', 'costo unit'],
  categoria: ['categoria', 'grupo', 'tipo', 'familia'],
};

// ¿Esta fila es un encabezado? Tiene que nombrar al menos la columna del
// insumo y la del stock (así saltamos bandas de título de la plantilla).
function detectarEncabezado(fila) {
  const celdas = (fila ?? []).map((c) => normalizarNombre(c));
  const idx = {};
  for (const [campo, alias] of Object.entries(ALIAS_COLUMNAS)) {
    const i = celdas.findIndex((c) => c && alias.includes(c));
    if (i >= 0) idx[campo] = i;
  }
  if (idx.nombre == null || idx.stock == null) return null;
  return idx;
}

// Orden posicional cuando el archivo no trae encabezado (= plantilla de mezo).
const POSICIONAL = { nombre: 0, unidad: 1, stock: 2, minimo: 3, costo: 4, categoria: 5 };

// ─── Validación fila por fila ───────────────────────────────────────────────

// matriz: array de filas (array de celdas), como sale de Excel o del CSV.
// Devuelve { validas, errores, conEncabezado }.
//   validas: [{ fila, nombre, unidad, stock, minimo, costo, categoria }]
//   errores: [{ fila, nombre, error }]
export function parsearFilasMaestro(matriz) {
  const filas = Array.isArray(matriz) ? matriz : [];
  let mapa = null;
  let inicio = 0;
  // Buscamos el encabezado en las primeras 10 filas (las plantillas de mezo
  // llevan banda de título arriba).
  for (let i = 0; i < Math.min(filas.length, 10); i++) {
    const m = detectarEncabezado(filas[i]);
    if (m) { mapa = m; inicio = i + 1; break; }
  }
  const conEncabezado = !!mapa;
  if (!mapa) mapa = POSICIONAL;

  const validas = [];
  const errores = [];
  const vistos  = new Map();   // nombre normalizado → nº de fila donde apareció

  for (let i = inicio; i < filas.length; i++) {
    const f = filas[i] ?? [];
    const numFila = i + 1;
    const celda = (campo) => (mapa[campo] == null ? '' : f[mapa[campo]] ?? '');
    const nombre = String(celda('nombre') ?? '').trim();
    const vacia  = f.every((c) => String(c ?? '').trim() === '');
    if (vacia) continue;                                    // filas en blanco del Excel
    if (!nombre) { errores.push({ fila: numFila, nombre: '', error: 'Sin nombre de insumo' }); continue; }

    const clave = normalizarNombre(nombre);
    if (vistos.has(clave)) {
      errores.push({ fila: numFila, nombre, error: `Duplicado (ya aparece en la fila ${vistos.get(clave)})` });
      continue;
    }

    const stock = parseNumero(celda('stock'));
    if (Number.isNaN(stock)) { errores.push({ fila: numFila, nombre, error: 'Stock vacío o no numérico' }); continue; }
    if (stock < 0)           { errores.push({ fila: numFila, nombre, error: 'Stock negativo' }); continue; }

    const unidadCruda = String(celda('unidad') ?? '').trim();
    const unidad = unidadCruda ? normalizarUnidad(unidadCruda) : 'ud';
    if (!unidad) {
      errores.push({ fila: numFila, nombre, error: `Unidad desconocida "${unidadCruda}" (usa g, kg, ml, L, ud, lb u oz)` });
      continue;
    }

    const minimoCrudo = String(celda('minimo') ?? '').trim();
    const minimo = minimoCrudo ? parseNumero(minimoCrudo) : null;
    if (minimo != null && (Number.isNaN(minimo) || minimo < 0)) {
      errores.push({ fila: numFila, nombre, error: 'Mínimo no numérico' });
      continue;
    }

    const costoCrudo = String(celda('costo') ?? '').trim();
    const costo = costoCrudo ? parseNumero(costoCrudo) : null;
    if (costo != null && (Number.isNaN(costo) || costo < 0)) {
      errores.push({ fila: numFila, nombre, error: 'Costo no numérico' });
      continue;
    }

    const categoria = String(celda('categoria') ?? '').trim() || null;

    vistos.set(clave, numFila);
    validas.push({ fila: numFila, nombre, unidad, stock, minimo, costo, categoria });
  }

  return { validas, errores, conEncabezado };
}

// ─── Preview: qué le pasa a cada ítem según el modo ─────────────────────────
//
// modo 'reemplazar' = conteo físico (el stock queda EXACTO como dice el Excel).
// modo 'sumar'      = compra (se suma a lo que hay).
// itemsExistentes: [{ id, nombre, stockActual, unidad }] (shape del servicio).
export function previewCarga(validas, itemsExistentes, modo = 'reemplazar') {
  const porNombre = new Map();
  for (const it of itemsExistentes ?? []) porNombre.set(normalizarNombre(it.nombre), it);
  const filas = (validas ?? []).map((f) => {
    const existente = porNombre.get(normalizarNombre(f.nombre)) ?? null;
    if (!existente) {
      return { ...f, estado: 'nuevo', itemId: null, stockAntes: 0, stockDespues: f.stock, diferencia: f.stock };
    }
    const antes   = Number(existente.stockActual) || 0;
    const despues = modo === 'sumar' ? antes + f.stock : f.stock;
    const dif     = despues - antes;
    const cambiaMeta = (f.unidad && f.unidad !== existente.unidad)
      || (f.costo != null && Number(f.costo) !== Number(existente.precioCosto ?? 0))
      || (f.minimo != null && Number(f.minimo) !== Number(existente.stockMinimo ?? NaN));
    return {
      ...f,
      estado:       dif !== 0 || cambiaMeta ? 'actualiza' : 'sin_cambio',
      itemId:       existente.id,
      stockAntes:   antes,
      stockDespues: despues,
      diferencia:   dif,
    };
  });
  const resumen = filas.reduce((acc, f) => { acc[f.estado] = (acc[f.estado] ?? 0) + 1; return acc; },
    { nuevo: 0, actualiza: 0, sin_cambio: 0 });
  return { filas, resumen };
}

// ─── Cobertura de recetas ───────────────────────────────────────────────────
//
// Productos ACTIVOS (disponibles) que se venden y NO descuentan nada: sin
// receta o con receta apagada/vacía. Es lo que el dueño debe completar para
// que el inventario maestro se mantenga solo.
export function calcularCobertura(productos) {
  const activos = (productos ?? []).filter((p) => p && p.disponible !== false);
  const descuenta = (p) => {
    const r = p.inventarioAsociado;
    return !!(r && r.activo && Array.isArray(r.items)
      && r.items.some((it) => it && it.itemId && Number(it.cantidadPorVenta) > 0));
  };
  const sinReceta = activos.filter((p) => !descuenta(p)).map((p) => ({ id: p.id, nombre: p.nombre }));
  return {
    totalProductos: activos.length,
    conReceta:      activos.length - sinReceta.length,
    sinReceta,
  };
}
